import { createContext, useContext } from 'react';
import type { Container } from '@/app/container';
import type { Session } from '@obraplan/core/application/session';
import type { NonConformity } from '@obraplan/core/domain/quality/non-conformity';
import { roomPlaceLabel } from '@obraplan/core/domain/quality/non-conformity-place';
import type { PlacesRequest } from '@obraplan/core/domain/quality/service-verification';
import { nomeDaPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';

/**
 * O cômodo e a peça da não conformidade, na tela (Fase 37, etapa 3 — D9, D10).
 *
 * O passo de qualidade é o mesmo nos três modais — a Conferência, a medição por equipe e o
 * lançamento do RDO —, e cada tela sabe o dia e a obra. Em vez de passar o carregador e a ação
 * de encerrar por três diálogos, a tela os põe num contexto, e o passo os lê. Sem contexto, o
 * passo funciona como antes: sem lugares e sem o aviso da remedição.
 */

/** Um cômodo do dia, com o rótulo que a tela mostra. */
export interface RoomOfDay {
  readonly positionId: string;
  readonly roomId: string;
  readonly label: string;
}

/** O que a frente × serviço tem no dia: as peças executadas e os cômodos com avanço apontado. */
export interface PlacesOfDay {
  readonly pieces: readonly string[];
  readonly rooms: readonly RoomOfDay[];
}

export interface NcPlacesTools {
  readonly loadPlaces: (input: {
    readonly activityId: string;
    readonly serviceId: string;
  }) => Promise<PlacesOfDay | undefined>;
  /**
   * Encerra a NC aberta de outro dia com a solução (D10). Ausente: quem mede não trata NC, e o
   * aviso aparece sem o botão. Devolve se encerrou.
   */
  readonly resolvePending?: ((nc: NonConformity, solution: string) => Promise<boolean>) | undefined;
}

export const NcPlacesContext = createContext<NcPlacesTools | undefined>(undefined);

export function useNcPlaces(): NcPlacesTools | undefined {
  return useContext(NcPlacesContext);
}

/** Uma escolha de lugar na NC: a chave guarda o que vai ao servidor. */
export interface PlaceOption {
  readonly key: string;
  readonly kind: 'PIECE' | 'ROOM';
  readonly label: string;
}

/** `P:E76-A` — a peça pelo nome, como o servidor a procura no dia. */
export function piecePlaceOptionKey(name: string): string {
  return `P:${name.trim()}`;
}

/** `R:posição|cômodo`. */
export function roomPlaceOptionKey(positionId: string, roomId: string): string {
  return `R:${positionId}|${roomId}`;
}

/** As chaves dos lugares que a NC já tem — o rascunho começa com eles marcados. */
export function savedPlaceKeys(nc: Pick<NonConformity, 'places'> | undefined): string[] {
  return (nc?.places ?? []).map((place) =>
    place.kind === 'PIECE'
      ? piecePlaceOptionKey(place.pieceName)
      : roomPlaceOptionKey(place.positionId, place.roomId),
  );
}

/** As chaves marcadas, como o servidor as pede (D9). */
export function placesToInput(keys: readonly string[]): PlacesRequest {
  const pieces: string[] = [];
  const rooms: { positionId: string; roomId: string }[] = [];
  for (const key of keys) {
    if (key.startsWith('P:')) pieces.push(key.slice(2));
    else if (key.startsWith('R:')) {
      const [positionId = '', roomId = ''] = key.slice(2).split('|');
      rooms.push({ positionId, roomId });
    }
  }
  return { pieces, rooms };
}

/**
 * As escolhas de lugar de um serviço: as peças e os cômodos do dia (o que o servidor leu e o que
 * o modal marcou agora) e os que a NC já tinha — a peça que ela desmarcou não é mais "executada
 * no dia", e continua na lista dela. Sem repetir: a peça pelo nome normalizado. As peças vêm
 * primeiro, na ordem natural do nome; os cômodos depois, pelo rótulo.
 */
export function placeOptions(input: {
  readonly ofDay?: PlacesOfDay | undefined;
  readonly draftPieces?: readonly string[] | undefined;
  readonly draftRooms?: readonly RoomOfDay[] | undefined;
  readonly saved?: Pick<NonConformity, 'places'> | undefined;
}): PlaceOption[] {
  const pecas = new Map<string, string>();
  const juntarPeca = (nome: string) => {
    const chave = nomeDaPeca(nome);
    if (!pecas.has(chave)) pecas.set(chave, nome.trim());
  };
  const comodos = new Map<string, string>();
  const juntarComodo = (room: RoomOfDay) => {
    const chave = roomPlaceOptionKey(room.positionId, room.roomId);
    if (!comodos.has(chave)) comodos.set(chave, room.label);
  };

  for (const place of input.saved?.places ?? []) {
    if (place.kind === 'PIECE') juntarPeca(place.pieceName);
    else juntarComodo({ ...place, label: roomPlaceLabel(place) });
  }
  for (const nome of input.ofDay?.pieces ?? []) juntarPeca(nome);
  for (const nome of input.draftPieces ?? []) juntarPeca(nome);
  for (const room of input.ofDay?.rooms ?? []) juntarComodo(room);
  for (const room of input.draftRooms ?? []) juntarComodo(room);

  return [
    ...[...pecas.values()]
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }))
      .map((nome) => ({ key: piecePlaceOptionKey(nome), kind: 'PIECE' as const, label: nome })),
    ...[...comodos.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b, 'pt-BR', { numeric: true }))
      .map(([key, label]) => ({ key, kind: 'ROOM' as const, label })),
  ];
}

/** A chave marcada é a mesma escolha? A peça compara pelo nome normalizado. */
export function samePlaceKey(a: string, b: string): boolean {
  if (a.startsWith('P:') && b.startsWith('P:')) {
    return nomeDaPeca(a.slice(2)) === nomeDaPeca(b.slice(2));
  }
  return a === b;
}

/**
 * O carregador dos lugares do dia, sobre as duas leituras que o modal já usa: as peças da frente
 * (`GetFrontPieces`, as executadas naquele dia) e os cômodos (`GetFrontRooms`, os com o último
 * apontamento naquele dia).
 */
export function placesLoader(
  container: Pick<Container, 'getFrontPieces' | 'getFrontRooms'>,
  session: Session,
  projectId: string,
  date: string,
): NcPlacesTools['loadPlaces'] {
  return async ({ activityId, serviceId }) => {
    const pedido = { projectId, activityId, serviceId, date };
    const [pecas, comodos] = await Promise.all([
      container.getFrontPieces.execute(pedido, session),
      container.getFrontRooms.execute(pedido, session),
    ]);
    return {
      pieces: pecas.ok
        ? pecas.value.pieces.filter((piece) => piece.executedHere).map((piece) => piece.name)
        : [],
      rooms:
        comodos.ok && comodos.value.byRoom
          ? comodos.value.rooms
              .filter((room) => room.recordedOn === date)
              .map((room) => ({
                positionId: room.positionId,
                roomId: room.roomId,
                label: roomPlaceLabel({
                  roomName: room.roomName,
                  positionLabel: room.positionLabel,
                }),
              }))
          : [],
    };
  };
}
